import React, { useRef, useState } from 'react';

import {
  FileUploadDropzone,
  FileUploadItem,
  FileUploadList,
} from '@/design-system/file-upload';

import {
  isAbortError,
  parseWarehouseError,
  warehouseApi,
} from '../api/warehouse.api';
import { linkCls } from './ui';

/** Matches WAREHOUSE_DOCUMENT_MAX_BYTES on the server. */
export const DOCUMENT_MAX_BYTES = 10 * 1024 * 1024;

export interface DocumentValue {
  url: string;
  name: string;
  size: number;
}

const isPdf = (file: File) =>
  file.type === 'application/pdf' || /\.pdf$/i.test(file.name);

/**
 * One PDF, uploaded the moment it is picked (straight to the main bucket), so
 * the form only ever carries a stored URL. "Tersimpan" therefore means stored;
 * a failed upload stays on screen with a retry and keeps the form from moving
 * on while it is in flight (`onBusyChange`).
 *
 * Every upload carries a token: removing or replacing the file while it is
 * uploading aborts that request and retires its token, so a late answer can
 * never put the removed file back.
 */
export const DocumentUpload: React.FC<{
  value: DocumentValue | null;
  onChange: (value: DocumentValue | null) => void;
  onBusyChange?: (busy: boolean) => void;
  testId?: string;
  /** The dropzone's heading and the accessible name of its file input. */
  title?: string;
}> = ({
  value,
  onChange,
  onBusyChange,
  testId,
  title = 'Pilih PDF surat jalan atau seret ke sini',
}) => {
  const [pending, setPending] = useState<File | null>(null);
  const [error, setError] = useState<string | null>(null);
  /** The upload whose answer still counts (null = none in flight). */
  const current = useRef<{ token: number; abort: AbortController } | null>(
    null
  );
  const tokens = useRef(0);
  const onBusyRef = useRef(onBusyChange);
  onBusyRef.current = onBusyChange;

  /** Retire the in-flight upload (its answer will be ignored). */
  const retire = () => {
    if (!current.current) return;
    current.current.abort.abort();
    current.current = null;
    onBusyRef.current?.(false);
  };

  const upload = async (file: File) => {
    retire();
    const mine = { token: ++tokens.current, abort: new AbortController() };
    current.current = mine;
    const live = () => current.current?.token === mine.token;
    setPending(file);
    setError(null);
    onBusyRef.current?.(true);
    try {
      const doc = await warehouseApi.uploadDocument(file, {
        signal: mine.abort.signal,
      });
      if (!live()) return;
      onChange({ url: doc.fileUrl, name: file.name, size: file.size });
      setPending(null);
    } catch (err) {
      if (!live() || isAbortError(err)) return;
      setError(parseWarehouseError(err).message || 'Gagal mengunggah');
    } finally {
      if (live()) {
        current.current = null;
        onBusyRef.current?.(false);
      }
    }
  };

  const pick = (files: File[]) => {
    const file = files[0];
    if (!file) return;
    if (!isPdf(file)) {
      retire();
      setPending(file);
      setError('File harus PDF.');
      return;
    }
    void upload(file);
  };

  const clear = () => {
    retire();
    setPending(null);
    setError(null);
    onChange(null);
  };

  if (value && !pending) {
    return (
      <div className="flex flex-col gap-1.5" data-testid={testId}>
        <FileUploadList>
          <FileUploadItem
            name={value.name}
            size={value.size}
            mime="application/pdf"
            status="complete"
            onRemove={clear}
          />
        </FileUploadList>
        <a
          href={value.url}
          target="_blank"
          rel="noreferrer"
          className={`${linkCls} text-[12px]`}
        >
          Buka PDF
        </a>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-2" data-testid={testId}>
      {pending ? (
        <FileUploadList>
          <FileUploadItem
            name={pending.name}
            size={pending.size}
            mime={pending.type || 'application/pdf'}
            status={error ? 'error' : 'uploading'}
            errorMessage={error ?? undefined}
            onRetry={
              error && isPdf(pending) ? () => void upload(pending) : undefined
            }
            onRemove={clear}
          />
        </FileUploadList>
      ) : (
        <FileUploadDropzone
          accept="application/pdf,.pdf"
          multiple={false}
          maxBytes={DOCUMENT_MAX_BYTES}
          onFiles={pick}
          title={title}
          description="PDF, maksimal 10 MB"
          browseLabel="Pilih file"
        />
      )}
    </div>
  );
};
